/**
 * The sections of a well testing job's page (WT-2, fieldiq-api#1245): crew,
 * equipment and packages, rent-ins and the shift-log list. Each is a
 * full-width panel; an add form opens inline in the panel (a row is a child of
 * the job), and a removal asks in-app first.
 *
 * Writes are WT_DISPATCH. A closed job takes no writes (409 job_closed), so its
 * forms are not offered. A refusal that still arrives reads as one sentence
 * from `wtRefusal`, never as a green toast.
 *
 * RENT-IN COST (rate, billable, PO) is drawn only when `canSeeCost` — the roles
 * the API sends it to. A field hand's payload has no cost keys, and this
 * section does not draw an empty cost column for them either.
 */
import React, { useMemo, useState } from 'react';
import { CalendarCheck, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import {
  useAddWtCrew,
  useAddWtJobEquipment,
  useAddWtRentIn,
  useRemoveWtCrew,
  useRemoveWtJobEquipment,
  useRemoveWtRentIn,
  useUpdateWtJobEquipment,
  useUpdateWtRentIn,
  useWtPackages,
  useWtShiftLogs,
} from '../../hooks/useWellTesting';
import { blankToNull, catalogLabel, fmtWtNumber, wtDay, wtRefusal, wtRentStatusSkin } from '../../lib/well-testing';
import type { WtCatalog, WtCrewMember, WtJobDetail, WtJobEquipment, WtRentIn, WtShiftLogSummary } from '../../types/well-testing';
import {
  WT_DATE_INPUT,
  WT_INPUT,
  WT_PRIMARY,
  WT_SECONDARY,
  WtBadge,
  WtDialog,
  WtEmpty,
  WtField,
  WtFormError,
  WtPanel,
  WtRegister,
  type WtColumn,
} from './WtBits';

const SETTLED = { isLoading: false };

function RemoveConfirm({
  open,
  title,
  body,
  confirmLabel,
  pending,
  error,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <WtDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WT_SECONDARY} onClick={onClose} disabled={pending}>
            Keep it
          </button>
          <button type="button" className={WT_PRIMARY} onClick={onConfirm} disabled={pending}>
            {pending ? 'Removing…' : confirmLabel}
          </button>
        </>
      }
    >
      <WtFormError message={error} />
      <p className="text-sm text-fieldiq-text">{body}</p>
    </WtDialog>
  );
}

function RemoveButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className={WT_SECONDARY} aria-label={label} onClick={onClick}>
      <Trash2 className="size-4" aria-hidden="true" />
      Remove
    </button>
  );
}

function AddButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className={WT_PRIMARY} onClick={onClick} disabled={disabled}>
      <Plus className="size-4" aria-hidden="true" />
      {label}
    </button>
  );
}

function FormButtons({ pending, onCancel, label }: { pending: boolean; onCancel: () => void; label: string }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" className={WT_SECONDARY} onClick={onCancel} disabled={pending}>
        Cancel
      </button>
      <button type="submit" className={WT_PRIMARY} disabled={pending}>
        {pending ? 'Saving…' : label}
      </button>
    </div>
  );
}

/** A date-pair dialog (off date / off-rent date): one field, its refusal inside. */
function DateDialog({
  open,
  title,
  label,
  initial,
  body,
  pending,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  title: string;
  label: string;
  initial: string;
  body: string;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (value: string | null) => void;
}) {
  const [value, setValue] = useState(initial);
  React.useEffect(() => {
    if (open) setValue(initial);
  }, [open, initial]);
  return (
    <WtDialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={WT_SECONDARY} onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button type="button" className={WT_PRIMARY} onClick={() => onSave(value || null)} disabled={pending}>
            {pending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <WtFormError message={error} />
      <p className="mb-3 text-sm text-fieldiq-text">{body}</p>
      <WtField label={label}>
        <input type="date" className={WT_DATE_INPUT} aria-label={label} value={value} onChange={(e) => setValue(e.target.value)} />
      </WtField>
    </WtDialog>
  );
}

// ─── Crew ────────────────────────────────────────────────────────────────────

export function CrewSection({
  job,
  catalog,
  users,
  canWrite,
}: {
  job: WtJobDetail;
  catalog: WtCatalog | null;
  users: { id: string; name: string }[];
  canWrite: boolean;
}) {
  const add = useAddWtCrew();
  const remove = useRemoveWtCrew();
  const [adding, setAdding] = useState(false);
  const blank = { user_id: '', crew_role: '', shift: '', start_on: '', end_on: '', notes: '' };
  const [form, setForm] = useState(blank);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<WtCrewMember | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const onCrew = useMemo(() => new Set(job.crew.map((c) => c.userId)), [job.crew]);
  const pickable = users.filter((u) => !onCrew.has(u.id));
  const roles = catalog?.crewRoles ?? [];
  const shifts = catalog?.shifts ?? [
    { value: 'day', label: 'Day' },
    { value: 'night', label: 'Night' },
  ];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.user_id) return setError('Choose who joins the crew.');
    if (!form.crew_role) return setError('Choose their role on the job.');
    if (form.start_on && form.end_on && form.end_on < form.start_on) return setError('The end day cannot be before the start day.');
    add.mutate(
      {
        jobId: job.id,
        body: {
          user_id: form.user_id,
          crew_role: form.crew_role,
          shift: blankToNull(form.shift),
          start_on: blankToNull(form.start_on),
          end_on: blankToNull(form.end_on),
          notes: blankToNull(form.notes),
        },
      },
      {
        onSuccess: () => {
          toast.success('Added to the crew');
          setForm(blank);
          setAdding(false);
        },
        onError: (err) => setError(wtRefusal(err, 'They could not be added to the crew.')),
      },
    );
  };

  const columns: WtColumn<WtCrewMember>[] = [
    { key: 'name', header: 'Name', render: (c) => <span className="font-extrabold text-white">{c.userName || '—'}</span> },
    { key: 'role', header: 'Role', render: (c) => c.crewRoleLabel || catalogLabel(roles, c.crewRole) },
    { key: 'shift', header: 'Shift', render: (c) => c.shiftLabel || catalogLabel(shifts, c.shift) },
    { key: 'dates', header: 'On the job', render: (c) => (c.startOn || c.endOn ? `${wtDay(c.startOn)} → ${wtDay(c.endOn)}` : 'Whole job') },
    { key: 'notes', header: 'Notes', render: (c) => c.notes || '—' },
  ];
  if (canWrite) columns.push({ key: 'remove', header: '', align: 'right', render: (c) => <RemoveButton label={`Remove ${c.userName ?? 'crew member'}`} onClick={() => { setRemoveError(null); setRemoving(c); }} /> });

  return (
    <WtPanel
      eyebrow="Crew"
      title="Crew"
      testId="wt-job-crew"
      actions={canWrite && !adding ? <AddButton label="Add crew" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="wt-crew-form">
          <WtFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <WtField label="Person">
              <select className={WT_INPUT} aria-label="Person" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
                <option value="">Choose…</option>
                {pickable.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </WtField>
            <WtField label="Role">
              <select className={WT_INPUT} aria-label="Crew role" value={form.crew_role} onChange={(e) => setForm({ ...form, crew_role: e.target.value })}>
                <option value="">Choose…</option>
                {roles.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </WtField>
            <WtField label="Shift">
              <select className={WT_INPUT} aria-label="Shift" value={form.shift} onChange={(e) => setForm({ ...form, shift: e.target.value })}>
                <option value="">Either</option>
                {shifts.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </WtField>
            <WtField label="Start day">
              <input type="date" className={WT_DATE_INPUT} aria-label="Start day" value={form.start_on} onChange={(e) => setForm({ ...form, start_on: e.target.value })} />
            </WtField>
            <WtField label="End day">
              <input type="date" className={WT_DATE_INPUT} aria-label="End day" value={form.end_on} onChange={(e) => setForm({ ...form, end_on: e.target.value })} />
            </WtField>
          </div>
          <WtField label="Notes">
            <input className={WT_INPUT} aria-label="Crew notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </WtField>
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add to crew" />
        </form>
      )}
      <WtRegister
        query={SETTLED}
        rows={job.crew}
        columns={columns}
        rail={() => 'border-l-transparent'}
        subject="the crew"
        testId="wt-crew"
        minWidth="min-w-[720px]"
        empty={<WtEmpty>No one is on this job's crew yet.</WtEmpty>}
        card={(c) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-white">{c.userName || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {c.crewRoleLabel || catalogLabel(roles, c.crewRole)} · {c.shiftLabel || 'Either shift'}
              </p>
            </div>
            {canWrite && <RemoveButton label={`Remove ${c.userName ?? 'crew member'}`} onClick={() => { setRemoveError(null); setRemoving(c); }} />}
          </div>
        )}
      />
      <RemoveConfirm
        open={!!removing}
        title="Remove from crew"
        body={`Takes ${removing?.userName ?? 'this person'} off the crew. Shift logs that already name them keep the name.`}
        confirmLabel="Remove"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Removed from the crew');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(wtRefusal(err, 'They could not be removed.')),
          })
        }
      />
    </WtPanel>
  );
}

// ─── Equipment and packages ──────────────────────────────────────────────────

export function EquipmentSection({
  job,
  equipment,
  canWrite,
}: {
  job: WtJobDetail;
  equipment: { id: string; name: string; unitNumber: string | null }[];
  canWrite: boolean;
}) {
  const add = useAddWtJobEquipment();
  const update = useUpdateWtJobEquipment();
  const remove = useRemoveWtJobEquipment();
  const packages = useWtPackages();
  const [adding, setAdding] = useState(false);
  const blank = { mode: 'package' as 'package' | 'unit', equipment_id: '', package_id: '', on_date: '', notes: '' };
  const [form, setForm] = useState(blank);
  const [error, setError] = useState<string | null>(null);
  const [offing, setOffing] = useState<WtJobEquipment | null>(null);
  const [offError, setOffError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<WtJobEquipment | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const onJob = useMemo(() => new Set(job.equipment.map((e) => e.equipmentId)), [job.equipment]);
  const pickable = equipment.filter((e) => !onJob.has(e.id));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (form.mode === 'unit' && !form.equipment_id) return setError('Choose the unit.');
    if (form.mode === 'package' && !form.package_id) return setError('Choose the package.');
    add.mutate(
      {
        jobId: job.id,
        body: {
          ...(form.mode === 'unit' ? { equipment_id: form.equipment_id } : { package_id: form.package_id }),
          on_date: blankToNull(form.on_date),
          notes: blankToNull(form.notes),
        },
      },
      {
        onSuccess: (res) => {
          const n = res.data.length;
          toast.success(n === 1 ? 'Unit added to the job' : `${n} units added to the job`);
          setForm(blank);
          setAdding(false);
        },
        onError: (err) => setError(wtRefusal(err, 'The equipment could not be added.')),
      },
    );
  };

  const columns: WtColumn<WtJobEquipment>[] = [
    {
      key: 'unit',
      header: 'Unit',
      render: (e) => (
        <>
          <p className="font-extrabold text-white">{e.equipmentName || '—'}</p>
          <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{e.unitNumber || e.serialNumber || '—'}</p>
        </>
      ),
    },
    { key: 'kind', header: 'Component', render: (e) => e.componentKindLabel || '—' },
    { key: 'package', header: 'Package', render: (e) => e.packageName || '—' },
    { key: 'dates', header: 'On / off', render: (e) => <span className="whitespace-nowrap">{`${wtDay(e.onDate)} → ${wtDay(e.offDate)}`}</span> },
    { key: 'notes', header: 'Notes', render: (e) => e.notes || '—' },
  ];
  if (canWrite)
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      render: (e) => (
        <span className="inline-flex gap-2 whitespace-nowrap">
          <button type="button" className={WT_SECONDARY} aria-label={`Set off date for ${e.equipmentName ?? 'unit'}`} onClick={() => { setOffError(null); setOffing(e); }}>
            <CalendarCheck className="size-4" aria-hidden="true" />
            Off date
          </button>
          <RemoveButton label={`Remove ${e.equipmentName ?? 'unit'}`} onClick={() => { setRemoveError(null); setRemoving(e); }} />
        </span>
      ),
    });

  return (
    <WtPanel
      eyebrow="Equipment"
      title="Equipment and packages on the job"
      testId="wt-job-equipment"
      actions={canWrite && !adding ? <AddButton label="Add equipment" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="wt-equipment-form">
          <WtFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <WtField label="Add">
              <select className={WT_INPUT} aria-label="Add a package or a unit" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value as 'package' | 'unit' })}>
                <option value="package">A whole package</option>
                <option value="unit">One unit</option>
              </select>
            </WtField>
            {form.mode === 'package' ? (
              <WtField label="Package">
                <select className={WT_INPUT} aria-label="Package" value={form.package_id} onChange={(e) => setForm({ ...form, package_id: e.target.value })}>
                  <option value="">{packages.isLoading ? 'Loading…' : 'Choose…'}</option>
                  {(packages.data?.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {displayValue(p.unitCount ?? undefined)} units{p.openJobNumber ? ` · on ${p.openJobNumber}` : ''}
                    </option>
                  ))}
                </select>
              </WtField>
            ) : (
              <WtField label="Unit">
                <select className={WT_INPUT} aria-label="Unit" value={form.equipment_id} onChange={(e) => setForm({ ...form, equipment_id: e.target.value })}>
                  <option value="">Choose…</option>
                  {pickable.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                      {u.unitNumber ? ` · ${u.unitNumber}` : ''}
                    </option>
                  ))}
                </select>
              </WtField>
            )}
            <WtField label="On date">
              <input type="date" className={WT_DATE_INPUT} aria-label="On date" value={form.on_date} onChange={(e) => setForm({ ...form, on_date: e.target.value })} />
            </WtField>
            <WtField label="Notes">
              <input className={WT_INPUT} aria-label="Equipment notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </WtField>
          </div>
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add to job" />
        </form>
      )}
      <WtRegister
        query={SETTLED}
        rows={job.equipment}
        columns={columns}
        rail={() => 'border-l-transparent'}
        subject="the job's equipment"
        testId="wt-job-unit"
        minWidth="min-w-[820px]"
        empty={<WtEmpty>No owned equipment is on this job yet. Add a package to bring its units on together.</WtEmpty>}
        card={(e) => (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-extrabold text-white">{e.equipmentName || '—'}</p>
              <p className="mt-0.5 text-xs text-fieldiq-muted">
                {e.componentKindLabel || '—'} · {e.packageName || 'No package'} · {wtDay(e.onDate)} → {wtDay(e.offDate)}
              </p>
            </div>
            {canWrite && <RemoveButton label={`Remove ${e.equipmentName ?? 'unit'}`} onClick={() => { setRemoveError(null); setRemoving(e); }} />}
          </div>
        )}
      />
      <DateDialog
        open={!!offing}
        title="Off date"
        label="Off date"
        initial={offing?.offDate ?? ''}
        body={`The last day ${offing?.equipmentName ?? 'this unit'} is on the job. Blank means it is still on.`}
        pending={update.isPending}
        error={offError}
        onClose={() => setOffing(null)}
        onSave={(value) =>
          offing &&
          update.mutate(
            { id: offing.id, body: { off_date: value } },
            {
              onSuccess: () => {
                toast.success('Off date saved');
                setOffing(null);
              },
              onError: (err) => setOffError(wtRefusal(err, 'The off date could not be saved.')),
            },
          )
        }
      />
      <RemoveConfirm
        open={!!removing}
        title="Take off the job"
        body={`Takes ${removing?.equipmentName ?? 'this unit'} off the job. A unit with hours on a shift log cannot be removed — set its off date instead.`}
        confirmLabel="Remove"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Taken off the job');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(wtRefusal(err, 'The unit could not be removed.')),
          })
        }
      />
    </WtPanel>
  );
}

// ─── Rent-ins ────────────────────────────────────────────────────────────────

export function RentInsSection({
  job,
  catalog,
  currency,
  canWrite,
  canSeeCost,
}: {
  job: WtJobDetail;
  catalog: WtCatalog | null;
  currency: string | null;
  canWrite: boolean;
  canSeeCost: boolean;
}) {
  const add = useAddWtRentIn();
  const update = useUpdateWtRentIn();
  const remove = useRemoveWtRentIn();
  const [adding, setAdding] = useState(false);
  const blank = {
    vendor_name: '', vendor_contact: '', description: '', unit_number: '', on_rent_on: '', off_rent_on: '',
    cost_rate: '', rate_unit: 'day', billable: true, po_number: '', notes: '',
  };
  const [form, setForm] = useState(blank);
  const [error, setError] = useState<string | null>(null);
  const [offing, setOffing] = useState<WtRentIn | null>(null);
  const [offError, setOffError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<WtRentIn | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const rateUnits = catalog?.rateUnits ?? [];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.vendor_name.trim()) return setError('Enter the vendor.');
    if (!form.description.trim()) return setError('Say what was rented.');
    if (form.on_rent_on && form.off_rent_on && form.off_rent_on < form.on_rent_on) return setError('The off-rent date cannot be before the on-rent date.');
    if (canSeeCost && form.cost_rate.trim() && !/^\d+(\.\d+)?$/.test(form.cost_rate.trim())) return setError('The rate must be a number, or blank.');
    add.mutate(
      {
        jobId: job.id,
        body: {
          vendor_name: form.vendor_name.trim(),
          vendor_contact: blankToNull(form.vendor_contact),
          description: form.description.trim(),
          unit_number: blankToNull(form.unit_number),
          on_rent_on: blankToNull(form.on_rent_on),
          off_rent_on: blankToNull(form.off_rent_on),
          notes: blankToNull(form.notes),
          // Cost is only sent by the roles that can see it.
          ...(canSeeCost
            ? { cost_rate: blankToNull(form.cost_rate), rate_unit: form.cost_rate.trim() ? form.rate_unit : null, billable: form.billable, po_number: blankToNull(form.po_number) }
            : {}),
        },
      },
      {
        onSuccess: () => {
          toast.success('Rent-in added');
          setForm(blank);
          setAdding(false);
        },
        onError: (err) => setError(wtRefusal(err, 'The rent-in could not be added.')),
      },
    );
  };

  const columns: WtColumn<WtRentIn>[] = [
    {
      key: 'what',
      header: 'Rented',
      render: (r) => (
        <>
          <p className="font-extrabold text-white">{r.description || '—'}</p>
          <p className="mt-0.5 text-xs text-fieldiq-muted">
            {r.vendorName || '—'}
            {r.unitNumber ? ` · ${r.unitNumber}` : ''}
          </p>
        </>
      ),
    },
    { key: 'dates', header: 'On / off rent', render: (r) => <span className="whitespace-nowrap">{`${wtDay(r.onRentOn)} → ${wtDay(r.offRentOn)}`}</span> },
    { key: 'days', header: 'Days', align: 'right', render: (r) => displayValue(r.onRentDays ?? undefined) },
    { key: 'hours', header: 'Hours', align: 'right', render: (r) => fmtWtNumber(r.loggedHours) },
  ];
  if (canSeeCost)
    columns.push(
      {
        key: 'rate',
        header: `Rate${currency ? ` (${currency})` : ''}`,
        align: 'right',
        render: (r) => (r.costRate == null ? '—' : `${fmtWtNumber(r.costRate)}${r.rateUnitLabel ? ` ${r.rateUnitLabel.toLowerCase()}` : ''}`),
      },
      { key: 'billable', header: 'Billable', render: (r) => (r.billable == null ? '—' : r.billable ? 'Yes' : 'No') },
      { key: 'po', header: 'PO', render: (r) => <span className="font-mono text-sm">{r.poNumber || '—'}</span> },
    );
  columns.push({ key: 'status', header: 'Status', render: (r) => <WtBadge skin={wtRentStatusSkin(r.rentStatus)} value={r.rentStatus} /> });
  if (canWrite)
    columns.push({
      key: 'actions',
      header: '',
      align: 'right',
      render: (r) => (
        <span className="inline-flex gap-2 whitespace-nowrap">
          <button type="button" className={WT_SECONDARY} aria-label={`Set off-rent date for ${r.description}`} onClick={() => { setOffError(null); setOffing(r); }}>
            <CalendarCheck className="size-4" aria-hidden="true" />
            Off rent
          </button>
          <RemoveButton label={`Remove ${r.description}`} onClick={() => { setRemoveError(null); setRemoving(r); }} />
        </span>
      ),
    });

  return (
    <WtPanel
      eyebrow="Rent-ins"
      title="Rented-in units"
      testId="wt-job-rent-ins"
      actions={canWrite && !adding ? <AddButton label="Add rent-in" onClick={() => { setError(null); setAdding(true); }} /> : undefined}
    >
      {adding && (
        <form onSubmit={submit} className="space-y-3 border-b border-fieldiq-border px-4 py-4" data-testid="wt-rent-in-form">
          <WtFormError message={error} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <WtField label="Vendor">
              <input className={WT_INPUT} aria-label="Vendor" value={form.vendor_name} onChange={(e) => setForm({ ...form, vendor_name: e.target.value })} />
            </WtField>
            <WtField label="Vendor contact">
              <input className={WT_INPUT} aria-label="Vendor contact" value={form.vendor_contact} onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })} />
            </WtField>
            <WtField label="What">
              <input className={WT_INPUT} aria-label="What was rented" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </WtField>
            <WtField label="Unit number">
              <input className={WT_INPUT} aria-label="Rent-in unit number" value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} />
            </WtField>
            <WtField label="On rent">
              <input type="date" className={WT_DATE_INPUT} aria-label="On rent" value={form.on_rent_on} onChange={(e) => setForm({ ...form, on_rent_on: e.target.value })} />
            </WtField>
            <WtField label="Off rent">
              <input type="date" className={WT_DATE_INPUT} aria-label="Off rent" value={form.off_rent_on} onChange={(e) => setForm({ ...form, off_rent_on: e.target.value })} />
            </WtField>
            {canSeeCost && (
              <>
                <WtField label={`Rate${currency ? ` (${currency})` : ''}`}>
                  <div className="flex gap-2">
                    <input className={WT_INPUT} inputMode="decimal" aria-label="Rate" value={form.cost_rate} onChange={(e) => setForm({ ...form, cost_rate: e.target.value })} />
                    <select className={`${WT_INPUT} w-32 shrink-0`} aria-label="Rate unit" value={form.rate_unit} onChange={(e) => setForm({ ...form, rate_unit: e.target.value })}>
                      {rateUnits.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </WtField>
                <WtField label="PO number">
                  <input className={WT_INPUT} aria-label="Rent-in PO number" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} />
                </WtField>
                <label className="flex items-center gap-2 self-end pb-2 text-sm text-fieldiq-text">
                  <input type="checkbox" checked={form.billable} onChange={(e) => setForm({ ...form, billable: e.target.checked })} />
                  Billable to the customer
                </label>
              </>
            )}
          </div>
          <WtField label="Notes">
            <input className={WT_INPUT} aria-label="Rent-in notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </WtField>
          <FormButtons pending={add.isPending} onCancel={() => setAdding(false)} label="Add rent-in" />
        </form>
      )}
      <WtRegister
        query={SETTLED}
        rows={job.rentIns}
        columns={columns}
        rail={(r) => wtRentStatusSkin(r.rentStatus).rail}
        subject="the rent-ins"
        testId="wt-rent-in"
        minWidth={canSeeCost ? 'min-w-[900px]' : 'min-w-[640px]'}
        empty={<WtEmpty>Nothing is rented in for this job.</WtEmpty>}
        card={(r) => (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-extrabold text-white">{r.description || '—'}</p>
                <p className="mt-0.5 text-xs text-fieldiq-muted">
                  {r.vendorName || '—'} · {wtDay(r.onRentOn)} → {wtDay(r.offRentOn)}
                </p>
              </div>
              <WtBadge skin={wtRentStatusSkin(r.rentStatus)} value={r.rentStatus} />
            </div>
            {canSeeCost && r.costRate != null && (
              <p className="mt-2 text-xs text-fieldiq-text">
                Rate {fmtWtNumber(r.costRate)} {r.rateUnitLabel?.toLowerCase() ?? ''} · {r.billable ? 'Billable' : 'Not billable'}
              </p>
            )}
          </>
        )}
      />
      <DateDialog
        open={!!offing}
        title="Off-rent date"
        label="Off rent"
        initial={offing?.offRentOn ?? ''}
        body={`The last day ${offing?.description ?? 'this unit'} is on rent. Blank means it is still on rent.`}
        pending={update.isPending}
        error={offError}
        onClose={() => setOffing(null)}
        onSave={(value) =>
          offing &&
          update.mutate(
            { id: offing.id, body: { off_rent_on: value } },
            {
              onSuccess: () => {
                toast.success('Off-rent date saved');
                setOffing(null);
              },
              onError: (err) => setOffError(wtRefusal(err, 'The off-rent date could not be saved.')),
            },
          )
        }
      />
      <RemoveConfirm
        open={!!removing}
        title="Remove rent-in"
        body={`Removes ${removing?.description ?? 'this rent-in'} from the job. One with hours on a shift log cannot be removed — set its off-rent date instead.`}
        confirmLabel="Remove"
        pending={remove.isPending}
        error={removeError}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              toast.success('Rent-in removed');
              setRemoving(null);
            },
            onError: (err) => setRemoveError(wtRefusal(err, 'The rent-in could not be removed.')),
          })
        }
      />
    </WtPanel>
  );
}

// ─── Shift logs ──────────────────────────────────────────────────────────────

export function LogsSection({
  job,
  canLog,
  onNew,
  onOpen,
}: {
  job: WtJobDetail;
  canLog: boolean;
  onNew: () => void;
  onOpen: (log: WtShiftLogSummary) => void;
}) {
  const query = useWtShiftLogs(job.id);
  const logs = query.data?.data ?? [];
  const columns: WtColumn<WtShiftLogSummary>[] = [
    { key: 'day', header: 'Day', render: (l) => <span className="whitespace-nowrap font-extrabold text-white">{wtDay(l.workDate)}</span> },
    { key: 'shift', header: 'Shift', render: (l) => l.shiftLabel || l.shift || '—' },
    { key: 'crew', header: 'Crew', align: 'right', render: (l) => displayValue(l.crewCount ?? undefined) },
    { key: 'hours', header: 'Hours', align: 'right', render: (l) => fmtWtNumber(l.hours) },
    { key: 'standby', header: 'Standby', align: 'right', render: (l) => fmtWtNumber(l.standbyHours) },
    { key: 'units', header: 'Units', align: 'right', render: (l) => displayValue(l.unitCount ?? undefined) },
    { key: 'owned', header: 'Owned hrs', align: 'right', render: (l) => fmtWtNumber(l.ownedEquipmentHours) },
    { key: 'rent', header: 'Rent-in hrs', align: 'right', render: (l) => fmtWtNumber(l.rentInHours) },
    { key: 'by', header: 'By', render: (l) => l.createdByName || '—' },
  ];
  return (
    <WtPanel
      eyebrow="Shift logs"
      title="Shift logs"
      testId="wt-job-logs"
      actions={canLog ? <AddButton label="New shift log" onClick={onNew} /> : undefined}
    >
      <WtRegister
        query={query}
        rows={logs}
        columns={columns}
        rail={() => 'border-l-transparent'}
        onOpen={onOpen}
        subject="the shift logs"
        testId="wt-shift-log"
        minWidth="min-w-[860px]"
        empty={<WtEmpty>No shift has been logged on this job yet.</WtEmpty>}
        card={(l) => (
          <>
            <p className="text-sm font-extrabold text-white">
              {wtDay(l.workDate)} · {l.shiftLabel || l.shift}
            </p>
            <p className="mt-1 text-xs text-fieldiq-text">
              Crew {displayValue(l.crewCount ?? undefined)} · {fmtWtNumber(l.hours)} h · Standby {fmtWtNumber(l.standbyHours)} h
            </p>
          </>
        )}
      />
    </WtPanel>
  );
}
